


import React, { useState, useEffect } from "react";
import {
  registerOrLogin,
  userResendOtp,
  forgotPassword,
  emailVerifyOtp,
  resenOtp,
  resetPassword,
  userLogin,
  userRegister,
  userSendLoginOtp,
  userVerifyLoginOtp,
  userVerifyEmailOtp,
} from "../services/authService";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../redux/slices/authSlice";
import { saveToken, saveUserData } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import "./AuthModal.css";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const STEP_LABELS = {
  2: "Login",
  3: "Create Account",
  4: "Verify Email",
  5: "Forgot Password",
  6: "Verify OTP",
  7: "Reset Password",
};

// Steps where progress bar makes sense (linear flow)
const STEP_ORDER = [2, 3, 4, 5, 6, 7];

function AuthModal({ show, onClose, setShowUserLogin }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step,         setStep]         = useState(2);
  const [loginMode,    setLoginMode]    = useState("otp");
  const [showPassword, setShowPassword] = useState(false);
  const [emailOtp,     setEmailOtp]     = useState("");
  const [message,      setMessage]      = useState("");
  const [loading,      setLoading]      = useState(false);
  const [loginOtpSent, setLoginOtpSent] = useState(false);
  const [resendTimer,  setResendTimer]  = useState(0);
  const [resending,    setResending]    = useState(false);
  const [errorMessage,   setErrorMessage]   = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [formData, setFormData] = useState({
    emailid: "", phonenumber: "", fullname: "",
    password: "", newPassword: "", confirmPassword: "",
  });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const closeModal = () => {
    onClose?.();
    setShowUserLogin(false);
    setLoginOtpSent(false);
    setEmailOtp("");
  };

  // ── Auto-clear messages ───────────────────────────────────────────────
  useEffect(() => {
    if (!errorMessage) return;
    const t = setTimeout(() => setErrorMessage(null), 5000);
    return () => clearTimeout(t);
  }, [errorMessage]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // ── Resend OTP countdown ─────────────────────────────────────────────
  useEffect(() => {
    if (![4, 6].includes(step)) return;
    setResendTimer(120);
    const iv = setInterval(() => {
      setResendTimer((p) => { if (p <= 1) { clearInterval(iv); return 0; } return p - 1; });
    }, 1000);
    return () => clearInterval(iv);
  }, [step]);

  // ── Save user info helper ─────────────────────────────────────────────
  const saveUserInfo = (res) => {
    saveUserData("safarix_user",         res.data.user);
    saveToken("safarix_token",           res.data.token);
    saveToken("safarix_refreshtoken",    res.data.refreshToken);
    saveUserData("safarix_login_time",   Date.now());
  };

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleLogin = async () => {
    try {
      setLoading(true);
      const res = await userLogin({ emailid: formData.emailid, password: formData.password });
      if (res.data.status === "otp_required") { setStep(4); return; }
      if (res.data.status === true) {
        dispatch(loginSuccess({ user: res.data.user, token: res.data.token }));
        saveUserInfo(res);
        closeModal();
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Login failed. Please try again.");
    } finally { setLoading(false); }
  };

  const handleSendLoginOtp = async () => {
    try {
      setLoading(true);
      const res = await userSendLoginOtp({ emailid: formData.emailid });
      if (res.data.status === "otp_sent") { setLoginOtpSent(true); setResendTimer(120); }
      setSuccessMessage("OTP sent successfully.");
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to send OTP.");
    } finally { setLoading(false); }
  };

  const handleVerifyLoginOtp = async () => {
    try {
      setLoading(true);
      const res = await userVerifyLoginOtp({ emailid: formData.emailid, otp: emailOtp });
      if (res.data.status === true) {
        dispatch(loginSuccess({ user: res.data.user, token: res.data.token }));
        saveUserInfo(res);
        closeModal();
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "OTP verification failed.");
    } finally { setLoading(false); }
  };

  const handleRegister = async () => {
    if (!formData.fullname || !formData.emailid || !formData.password || !formData.phonenumber) {
      setErrorMessage("All fields are required."); return;
    }
    try {
      setLoading(true);
      const res = await userRegister(formData);
      if (res?.data?.status === "otp_required") { setMessage(res.data.message); setStep(4); }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Registration failed.");
    } finally { setLoading(false); }
  };

  const verifyEmailOtp = async () => {
    try {
      setLoading(true);
      const res = await userVerifyEmailOtp({ emailid: formData.emailid, otp: emailOtp });
      if (res.data.status === true) {
        dispatch(loginSuccess({ user: res.data.user, token: res.data.token }));
        saveUserInfo(res);
        closeModal();
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "OTP verification failed.");
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      setResending(true);
      await userResendOtp({ emailid: formData.emailid });
      setResendTimer(120);
      setSuccessMessage("OTP resent successfully.");
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to resend OTP.");
    } finally { setResending(false); }
  };

  const VerifyforgotPassword = async () => {
    try {
      setLoading(true);
      await forgotPassword({ email: formData.emailid });
      setStep(6);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to send reset OTP.");
    } finally { setLoading(false); }
  };

  const VerifyforgotEmailOtp = async () => {
    try {
      setLoading(true);
      await emailVerifyOtp({ email: formData.emailid, otp: emailOtp });
      setStep(7);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "OTP verification failed.");
    } finally { setLoading(false); }
  };

  const ResendforgotOtp = async () => {
    try {
      setLoading(true);
      const res = await resenOtp({ email: formData.emailid });
      setSuccessMessage(res.data?.message || "OTP resent successfully.");
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to resend OTP.");
    } finally { setLoading(false); }
  };

  const ResetUserPassword = async () => {
    try {
      setLoading(true);
      const res = await resetPassword({
        email: formData.emailid,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      setSuccessMessage(res.data?.message || "Password reset successfully.");
      setLoginMode("password");
      setStep(2);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Password reset failed.");
    } finally { setLoading(false); }
  };

  if (!show) return null;

  return (
    <div
      className="am-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    >
      <div className="am-box">

        {/* ── Header ── */}
        <div className="am-header">
          <div className="am-header-left">
            <div className="am-logo">S</div>
            <div>
              <div className="am-header-title">User</div>
              <div className="am-header-sub">{STEP_LABELS[step]}</div>
            </div>
          </div>
          <button className="am-close" onClick={closeModal}>×</button>
        </div>

        {/* ── Step dots ── */}
        <div className="am-steps">
          {/* Login flow: steps 2→3→4  |  Forgot flow: steps 5→6→7 */}
          {[1, 2, 3].map((s) => {
            const loginDone  = [2, 3, 4].indexOf(step) >= s - 1 && step <= 4;
            const forgotDone = [5, 6, 7].indexOf(step) >= s - 1 && step >= 5;
            return (
              <div
                key={s}
                className={`am-step ${loginDone || forgotDone ? "am-step--done" : ""}`}
              />
            );
          })}
        </div>

        {/* ── Body ── */}
        <div className="am-body">

          {/* Messages */}
          {errorMessage   && <div className="am-alert am-alert--error">{errorMessage}</div>}
          {successMessage && <div className="am-alert am-alert--success">{successMessage}</div>}

          {/* ─── STEP 2 — Login ─── */}
          {step === 2 && (
            <>
              <p className="am-tagline">
                Welcome back! Sign in to <strong>Safarix</strong>
              </p>

              {/* Toggle */}
              <div className="am-toggle">
                <button
                  className={`am-toggle-btn${loginMode === "otp" ? " am-toggle-btn--active" : ""}`}
                  onClick={() => { setLoginMode("otp"); setLoginOtpSent(false); setEmailOtp(""); }}
                >
                  Login with OTP
                </button>
                <button
                  className={`am-toggle-btn${loginMode === "password" ? " am-toggle-btn--active" : ""}`}
                  onClick={() => { setLoginMode("password"); setLoginOtpSent(false); setEmailOtp(""); }}
                >
                  Login with Password
                </button>
              </div>

              <div className="am-field">
                <label className="am-label">Email address</label>
                <input
                  className="am-input"
                  type="email" name="emailid" placeholder="you@example.com"
                  value={formData.emailid} onChange={handleChange}
                  disabled={loginOtpSent}
                />
              </div>

              {/* OTP mode */}
              {loginMode === "otp" && (
                <>
                  {!loginOtpSent ? (
                    <button
                      className="am-btn am-btn--primary"
                      disabled={!isValidEmail(formData.emailid) || loading}
                      onClick={handleSendLoginOtp}
                    >
                      {loading ? <><span className="am-spinner" /> Sending…</> : "Send OTP"}
                    </button>
                  ) : (
                    <>
                      <div className="am-field">
                        <label className="am-label">OTP Code</label>
                        <input
                          className="am-input am-input--otp"
                          type="text" inputMode="numeric" maxLength={4}
                          placeholder="• • • •"
                          value={emailOtp}
                          onChange={(e) => setEmailOtp(e.target.value.replace(/\D/, ""))}
                        />
                      </div>
                      <button
                        className="am-btn am-btn--primary"
                        disabled={emailOtp.length !== 4 || loading}
                        onClick={handleVerifyLoginOtp}
                      >
                        {loading ? <><span className="am-spinner" /> Verifying…</> : "Verify & Login"}
                      </button>
                      <div className="am-resend">
                        {resendTimer > 0
                          ? <span className="am-resend-timer">Resend in {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, "0")}</span>
                          : <button className="am-link" disabled={resending} onClick={handleResendOtp}>{resending ? "Resending…" : "Resend OTP"}</button>
                        }
                      </div>
                      <button className="am-link am-link--center" onClick={() => { setLoginOtpSent(false); setEmailOtp(""); }}>
                        Use different email
                      </button>
                    </>
                  )}
                </>
              )}

              {/* Password mode */}
              {loginMode === "password" && (
                <>
                  <div className="am-field">
                    <label className="am-label">Password</label>
                    <div className="am-input-wrap">
                      <input
                        className="am-input"
                        type={showPassword ? "text" : "password"}
                        name="password" placeholder="Enter your password"
                        value={formData.password} onChange={handleChange}
                      />
                      <button className="am-eye" type="button" onClick={() => setShowPassword(p => !p)}>
                        {showPassword ? "🙈" : "👁"}
                      </button>
                    </div>
                  </div>
                  <button
                    className="am-btn am-btn--primary"
                    disabled={!isValidEmail(formData.emailid) || !formData.password || loading}
                    onClick={handleLogin}
                  >
                    {loading ? <><span className="am-spinner" /> Signing in…</> : "Login"}
                  </button>
                </>
              )}

              <div className="am-links">
                <button className="am-link" onClick={() => setStep(5)}>Forgot password?</button>
                <button className="am-link" onClick={() => setStep(3)}>New user? Register →</button>
              </div>
            </>
          )}

          {/* ─── STEP 3 — Register ─── */}
          {step === 3 && (
            <>
              <p className="am-tagline">Create your <strong>Safarix</strong> account</p>

              <div className="am-field">
                <label className="am-label">Full name</label>
                <input className="am-input" type="text" name="fullname" placeholder="Your full name" value={formData.fullname} onChange={handleChange} />
              </div>
              <div className="am-field">
                <label className="am-label">Email address</label>
                <input className="am-input" type="email" name="emailid" placeholder="you@example.com" value={formData.emailid} onChange={handleChange} />
              </div>
              <div className="am-field">
                <label className="am-label">Phone number</label>
                <input className="am-input" type="tel" name="phonenumber" placeholder="10-digit mobile" maxLength={10} value={formData.phonenumber} onChange={handleChange} />
              </div>
              <div className="am-field">
                <label className="am-label">Password</label>
                <div className="am-input-wrap">
                  <input
                    className="am-input"
                    type={showPassword ? "text" : "password"}
                    name="password" placeholder="Create a strong password"
                    value={formData.password} onChange={handleChange}
                  />
                  <button className="am-eye" type="button" onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
              </div>

              <button
                className="am-btn am-btn--primary"
                disabled={loading}
                onClick={handleRegister}
              >
                {loading ? <><span className="am-spinner" /> Creating…</> : "Create Account"}
              </button>
              <div className="am-links">
                <button className="am-link" onClick={() => setStep(2)}>← Already registered? Login</button>
              </div>
            </>
          )}

          {/* ─── STEP 4 — Verify email OTP ─── */}
          {step === 4 && (
            <>
              <p className="am-tagline">
                {message || <>We've sent a code to <strong>{formData.emailid}</strong></>}
              </p>

              <div className="am-field">
                <label className="am-label">OTP Code</label>
                <input
                  className="am-input am-input--otp"
                  type="text" inputMode="numeric" maxLength={4} placeholder="• • • •"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/, ""))}
                />
              </div>

              <button
                className="am-btn am-btn--primary"
                disabled={emailOtp.length !== 4 || loading}
                onClick={verifyEmailOtp}
              >
                {loading ? <><span className="am-spinner" /> Verifying…</> : "Verify OTP"}
              </button>

              <div className="am-resend">
                {resendTimer > 0
                  ? <span className="am-resend-timer">Resend in {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, "0")}</span>
                  : <button className="am-link" disabled={resending} onClick={handleResendOtp}>{resending ? "Resending…" : "Resend OTP"}</button>
                }
              </div>
            </>
          )}

          {/* ─── STEP 5 — Forgot: enter email ─── */}
          {step === 5 && (
            <>
              <p className="am-tagline">Enter your registered email to reset your password.</p>

              <div className="am-field">
                <label className="am-label">Email address</label>
                <input className="am-input" type="email" name="emailid" placeholder="you@example.com" value={formData.emailid} onChange={handleChange} />
              </div>

              <button
                className="am-btn am-btn--primary"
                disabled={!isValidEmail(formData.emailid) || loading}
                onClick={VerifyforgotPassword}
              >
                {loading ? <><span className="am-spinner" /> Sending…</> : "Send Reset OTP"}
              </button>
              <div className="am-links">
                <button className="am-link" onClick={() => setStep(2)}>← Back to login</button>
              </div>
            </>
          )}

          {/* ─── STEP 6 — Forgot: verify OTP ─── */}
          {step === 6 && (
            <>
              <p className="am-tagline">
                Enter the code sent to <strong>{formData.emailid}</strong>
              </p>

              <div className="am-field">
                <label className="am-label">OTP Code</label>
                <input
                  className="am-input am-input--otp"
                  type="text" inputMode="numeric" maxLength={4} placeholder="• • • •"
                  value={emailOtp}
                  onChange={(e) => setEmailOtp(e.target.value.replace(/\D/, ""))}
                />
              </div>

              <button
                className="am-btn am-btn--primary"
                disabled={emailOtp.length !== 4 || loading}
                onClick={VerifyforgotEmailOtp}
              >
                {loading ? <><span className="am-spinner" /> Verifying…</> : "Verify OTP"}
              </button>

              <div className="am-resend">
                {resendTimer > 0
                  ? <span className="am-resend-timer">Resend in {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, "0")}</span>
                  : <button className="am-link" disabled={resending} onClick={ResendforgotOtp}>{resending ? "Resending…" : "Resend OTP"}</button>
                }
              </div>
            </>
          )}

          {/* ─── STEP 7 — Reset password ─── */}
          {step === 7 && (
            <>
              <p className="am-tagline">Choose a new strong password.</p>

              <div className="am-field">
                <label className="am-label">New Password</label>
                <div className="am-input-wrap">
                  <input
                    className="am-input"
                    type={showPassword ? "text" : "password"}
                    name="newPassword" placeholder="New password"
                    value={formData.newPassword} onChange={handleChange}
                  />
                  <button className="am-eye" type="button" onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? "🙈" : "👁"}
                  </button>
                </div>
              </div>
              <div className="am-field">
                <label className="am-label">Confirm Password</label>
                <input
                  className="am-input"
                  type="password" name="confirmPassword" placeholder="Confirm new password"
                  value={formData.confirmPassword} onChange={handleChange}
                />
              </div>

              <button
                className="am-btn am-btn--primary"
                disabled={!formData.newPassword || !formData.confirmPassword || loading}
                onClick={ResetUserPassword}
              >
                {loading ? <><span className="am-spinner" /> Resetting…</> : "Reset Password"}
              </button>
            </>
          )}

        </div>
        {/* end am-body */}

        {/* ── Footer ── */}
        <div className="am-footer">
          By signing in or registering, you accept our{" "}
          <a href="/terms-conditions" className="am-footer-link">Terms & Conditions</a>
          {" "}and{" "}
          <a href="/privacy-policy" className="am-footer-link">Privacy Policy</a>.
        </div>

      </div>
    </div>
  );
}

export default AuthModal;